import { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useFormikContext, useField } from 'formik'
import { useCallback, useMemo, useEffect } from 'react'
import { useGetSalesTeamsByBranch } from '@user/store/salesTeam'
import { ISalesTeam } from '@user/store/salesTeam'
import {
  useGetSalesTeamPermissionMessages,
  useGetSalesTeamUsers,
} from '@user/store/salesTeam'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

type Props = {
  branchId: string
  name: string
}

export function useData(props: Props) {
  const { branchId, name } = props
  const tidalThemeActive = useTidalThemeActive()

  const options = useGetSalesTeamsByBranch(branchId)

  const {
    setFieldValue,
    isSubmitting,
    values: { salesTeamId, assigneeId },
  } = useFormikContext<IAddOpportunityFormModel>()

  const [{}, { error, touched }] = useField<string[]>({
    name,
    validate: (value) => {
      if (true && !value) {
        return 'Required'
      }
    },
  })

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const { execute: loadSalesTeamUsers } = useGetSalesTeamUsers(
    options?.length == 1
      ? +options[0].id
      : !!salesTeamId
      ? +salesTeamId
      : undefined,
  )

  const getOptionLabel = useCallback(
    (option: ISalesTeam) => `${option?.name} - ${option?.branchName}`,
    [],
  )

  const getId = useCallback((option: ISalesTeam) => {
    return String(option.id)
  }, [])

  const { selectedSalesTeamAccessText } = useGetSalesTeamPermissionMessages(
    'success',
    salesTeamId ? parseInt(salesTeamId) : -1,
    assigneeId,
  )

  const defaultSalesTeam = useMemo(() => {
    return options.find((x) => x.branchDefault)
  }, [options])

  useEffect(() => {
    if (!salesTeamId && options?.length == 1) {
      setFieldValue(name, options[0].id)
    }
  }, [salesTeamId, setFieldValue])

  const initialOption = useMemo(() => {
    if (salesTeamId) {
      const value = options.find((x) => x.id === parseInt(salesTeamId))
      if (value) return value
    }
    return null
  }, [options, salesTeamId])

  useEffect(() => {
    if (defaultSalesTeam && !salesTeamId) {
      setFieldValue('salesTeamId', defaultSalesTeam.id)
      loadSalesTeamUsers(defaultSalesTeam.id)
    }

    if (salesTeamId == '' && initialOption) {
      setFieldValue('salesTeamId', initialOption.id)
      loadSalesTeamUsers(initialOption.id)
    }
  }, [
    salesTeamId,
    initialOption,
    setFieldValue,
    loadSalesTeamUsers,
    defaultSalesTeam,
  ])

  const height = tidalThemeActive ? '65px' : '54px'

  return {
    loadSalesTeamUsers,
    getOptionLabel,
    getId,
    options,
    initialOption,
    selectedSalesTeamAccessText,
    isSubmitting,
    showError,
    helperText,
    height,
  }
}
